<template>
  <div class="content article">
    <div class="article-body">
      <h1 class="article-title">The Data</h1>
      <div class="card">
        <div class="card-content">
          <p>There are four data sets shown on this site.</p>

          <ol>
            <li><a href="#sdot">Seattle Department of Transportation (SDOT) Collisions</a></li>
            <li><a href="#costs">Economic costs of collision severity</a></li>
            <li><a href="#neighborhoods">Seattle neighborhood geospatial boundaries</a></li>
            <li><a href="#districts">Seattle City Council District geospatial boundaries</a></li>
          </ol>

          <h2><a id="sdot"></a>Seattle Department of Transportation (SDOT) Collisions</h2>

          <p>
            According to description on the <a href="//data.seattle.gov/Transportation/SDOT-Collisions/v7k9-7dn4/about">SDOT Collisions about page</a> as of Sunday May 21, 2017 10:52pm PST, this data is described as...
          </p>

          <blockquote>
            "Displays the locations and attributes of collisions that occur within Seattle. All collisions are provided by the Seattle Police Department and Washington State Department of Transportation and are recorded by Traffic Records for the last 10 years. This includes all types of collisions. Collisions are shown at the intersection or mid-block of a street segment that is closest to where it occurred."
          </blockquote>

          <p>
            There is a <a href="//data.seattle.gov/api/assets/486A95FC-CA9B-4360-97E0-58FB5C35DA5F?download=true">PDF that describes the data fields available</a>. There is also a <a href="//dev.socrata.com/foundry/data.seattle.gov/v7k9-7dn4">json feed to the data provided by Socrata</a>. Socrata is the company hired by the city to host their open data. They provide the platform &amp; software. However, it is up to the city to use Socrata, enter the data, make sure the data is accurate, and answer questions about the data. 
          </p>

          <p>
            Building this site required combining the knowledge stored in these two different places. The knowledge stored in the PDF and the knowledge of how to use Socrata's API. The PDF from SDOT tells us what the data means and Socrata's developer portal tells us how to access the data and what format it is stored in. 
          </p>

          <h2><a id="costs"></a>Economic costs of collision severity</h2>
          
          <p>
            Every year the <a href="//www.nsc.org">National Safety Council (NSC)</a> publishes their <a href="http://www.nsc.org/learn/safety-knowledge/Pages/injury-facts.aspx">Injury Facts®</a> report. This report includes an estimated "Cost of Motor-Vehicle Injuries". Here is a <a href="//www.nsc.org/NSCDocuments_Corporate/estimating-costs.pdf">sample of a Cost of Motor-Vehicle Injuries report</a>. The National Safety Council categorizes collisions with the following six levels of severity. 
          </p>

          <ol>
            <li>Death</li>
            <li>Disabling</li>
            <li>Evident</li>
            <li>Possible</li>
            <li>No injury observed</li>
            <li>Property damage only</li>
          </ol>

          <p>
            The data from the Seattle Department of Transportation (SDOT) has the following five levels of severity. 
          </p>

          <ol>
            <li>Fatality collision (sdot code 3)</li>
            <li>Serious injury collision (sdot code 2b)</li>
            <li>Injury (sdot code 2)</li>
            <li>Unknown (sdot code 0)</li>
            <li>Property damage only collision (sdot code 1)</li>
          </ol>
          
          <p>
            We mapped the NSC severities to the SDOT severities as follows.  
          </p>

          <ol>
            <li>Death (nsc) -> Fatality collision (sdot code 3)</li>
            <li>Disabling (nsc) -> Serious injury collision (sdot code 2b)</li>
            <li>Evident (nsc) -> Injury (sdot code 2)</li>
            <li>Possible (nsc) -> Unknown (sdot code 0)</li>
            <li>Property damage only (nsc) -> Property damage only collision (sdot code 1)</li>
          </ol>

          <p>
            The National Safety Council also has TWO sets of estimated costs, "economic" and "comprehensive" costs. In the <a href="//www.nsc.org/NSCDocuments_Corporate/estimating-costs.pdf">sample report</a> the National Safety Council says the following about the meaning of "Economic Costs".
          </p>

          <blockquote>
            "The figures above are appropriate for measuring the economic loss to a community resulting from past motor-vehicle crashes. They should not be used, however, in computing the dollar value of future benefits due to traffic safety measures because they do not include the value of a person's natural desire to live longer or to protect the quality of one's life. That is, the economic loss estimates do not include what people are willing to pay for improved safety."
          </blockquote>
          
          <p>
            So, the "economic cost" is basically the cost of the <em>past damage</em> done by collisions where comprehensive costs includes a person's desires for the future. Notice in the <a href="//www.nsc.org/NSCDocuments_Corporate/estimating-costs.pdf">sample report</a>, the economic costs are lower than the comprehensive cost since economic costs do not include benefits gained from safety improvement investments and changes in quality of life of those involved in a collision.
          </p>

          <p>
            <a href="//www.linkedin.com/in/cathytuttle">Cathy Tuttle</a> recommended we use the more conservative economic costs number. Below is a table for the economic costs used for the calculations in this application. The headings are the NSC label / SDOT label. It would be nice to have the ability to switch between economic &amp; comprehensive costs. I have this on my <router-link :to="{name: 'toDo'}">to do list</router-link>. 
          </p>

          <table class="teal small">
            <thead>
              <tr>
                <th>Year</th>
                <th>Death / Fatality</th>
                <th>Disabling / Serious injury</th>
                <th>Evident injury / Injury</th>
                <th>Possible injury / Severity unknown</th>
                <th>Property damage only / property damage</th>
                <th>Notes</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(cost, year) in costs">
                <td>{{year}}</td>
                <td v-for="dollars in cost">${{ Number(dollars.amount).toLocaleString('en-US') }}</td>
                <td>{{cost[0].desc}}</td>
              </tr>
            </tbody>
          </table>

          <h2><a id="neighborhoods"></a>Seattle neighborhood geospatial boundaries</h2>

          <p>
            The <a href="//data.seattle.gov/Transportation/SDOT-Collisions/v7k9-7dn4/about">data provided by the Seattle Department of Transportation</a> includes latitude &amp; longitude coordinates for a collision. However, we don't know what neighborhood these coodinates fall in. I wanted to be able to view collisions by neighborhood so I had to find neighborhoood geospatial boundaries. Turns out that <a href="https://www.zillow.com/howto/api/neighborhood-boundaries.htm">Zillow has neighborhood boundaries in shapefiles</a>. However, this does me little good since I need the coordinates in a format that I can store in my database. 
          </p>

          <p>
            After some searching on GitHub I found a <a href="//github.com/mashvisor/us-neighborhoods-boundaries">repository by Mashvisor that has the Zillow shape files converted into GeoJSON and CSV</a>. The <a href="//github.com/mashvisor/us-neighborhoods-boundaries/blob/master/out/csv/WA-Regions.csv">.csv file for Washington State</a> is the one I used to create the multipolygon datatypes in my database. 
          </p>

          <h2><a id="districts"></a>Seattle City Council geospatial boundaries</h2>

          <p>
            <a href="//www.linkedin.com/in/cathytuttle">Cathy</a> &amp; <a href="//www.linkedin.com/in/gordonpadelford">Gordon</a> mentioned that it would be interesting to see collisions by district. I attened an <a href="http://openseattle.org/">Open Seattle</a> meetup and <a href="https://github.com/JacksonMaxfield">Jackson Maxfield</a> introduced me to <a href="http://seattle.io">Seattle.io</a>. I don't know why I didn't come across this before! Turns out there are <a href="https://github.com/seattleio/seattle-boundaries-data/tree/master/data">a bunch of boundaries available</a> here including <a href="https://github.com/seattleio/seattle-boundaries-data/blob/master/data/city-council-districts.geojson">City Council Districts</a>. Yay!
          </p>

          <p>
            With some refactoring from my code to create the neighborhood boundaries, I imported the City Council District boundaries, created similar API endpoints, and similar views. I'd like to make these district views better. Perhaps show a list of neighborhoods within each district!
          </p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios'
import * as Util from '@/vuex/util'

export default {
  name: 'data',

  data: function () {
    return {
      costs: ''
    }
  },

  mounted () {
    let vm = this
    axios.get(Util.makeApiFilterUrl('/costs')).then(function (response) {
      vm.costs = response.data
    }).catch(function (error) {
      console.log(error)
    })
  }
}
</script>

<style lang="scss">
@import "./src/sass/views/article";
@import "./src/sass/card";
</style>



// WEBPACK FOOTER //
// Data.vue?73b0c8b5